/**
 * Message types for analysis WebSocket communication
 */

/** Base message type for all WebSocket messages */
export type AnalysisMessageType = 'START' | 'PROGRESS' | 'STAGE_COMPLETION' | 'COMPLETION' | 'ERROR' | 'PING' | 'PONG';

/** Base interface for all analysis messages */
export interface BaseAnalysisMessage {
  task_id: string;
  type: AnalysisMessageType;
}

/** Message sent when analysis task starts */
export interface StartMessage extends BaseAnalysisMessage {
  type: 'START';
  processing_type: string;
  data: {
    progress: number;
  };
}

/** Message sent for progress updates */
export interface ProgressMessage extends BaseAnalysisMessage {
  type: 'PROGRESS';
  data: {
    stage_name: string;
    progress: number;
    details?: Record<string, unknown>;
  };
}

/** Message sent when a processing stage completes */
export interface StageCompletionMessage extends BaseAnalysisMessage {
  type: 'STAGE_COMPLETION';
  data: {
    stage_name: string;
    result?: Record<string, unknown>;
  };
}

/** Message sent when analysis task completes */
export interface CompletionMessage extends BaseAnalysisMessage {
  type: 'COMPLETION';
  processing_type: string;
  status: 'SUCCESSFUL' | 'WITH_ERROR';
  data?: Record<string, unknown>;
  error?: Record<string, unknown>;
}

/** Message sent when an error occurs */
export interface ErrorMessage extends BaseAnalysisMessage {
  type: 'ERROR';
  error: {
    code: string;
    message: string;
    severity: 'RECOVERABLE' | 'FATAL';
  };
}

/** Message sent for heartbeat */
export interface PingMessage extends BaseAnalysisMessage {
  type: 'PING';
}

/** Message sent in response to ping */
export interface PongMessage extends BaseAnalysisMessage {
  type: 'PONG';
}

/** Union type for all possible analysis messages */
export type AnalysisMessage = StartMessage | ProgressMessage | StageCompletionMessage | CompletionMessage | ErrorMessage | PingMessage | PongMessage;

/** Socket connection states */
export enum SocketState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  RECONNECTING = 'RECONNECTING',
  CLOSED = 'CLOSED',
  ERROR = 'ERROR'
}

/** Alias for AnalysisMessage for better readability */
export type Message = AnalysisMessage;

/** Enum for message types */
export enum MessageType {
  START = 'START',
  PROGRESS = 'PROGRESS',
  STAGE_COMPLETION = 'STAGE_COMPLETION',
  COMPLETION = 'COMPLETION',
  ERROR = 'ERROR',
  PING = 'PING',
  PONG = 'PONG'
} 